/**
 * Svelte-specific utilities for css-motion
 */

import {
  AnimationConfig,
  getOnLoadClass,
  getOnViewClass,
  getVarsStyle,
  IntersectionObserverOptions,
} from '../core';
import { createAttachmentKey } from 'svelte/attachments';
import { observeOnView } from './attachments';

export * from './attachments';

/**
 * Animation helper object with onLoad and onView methods
 * Provides a cleaner, more organized API for animations
 *
 * @example
 * ```typescript
 * const { style, class: className } = animate.onLoad(presets.fadeIn());
 * const { styleObject, class: className } = animate.onLoad.object(presets.fadeIn());
 * ```
 */
export const animate = {
  /**
   * Creates animation configuration for load-triggered animations
   * Returns both CSS variables string and the appropriate class name
   *
   * @example
   * ```typescript
   * const { style, class: className } = animate.onLoad(presets.fadeIn());
   * // Returns: { style: "--anim-opacity-start: 0; --anim-duration: 0.5s", class: "css-motion css-motion--load" }
   * ```
   */
  onLoad: (
    config: AnimationConfig,
    { class: className }: { class?: string } = {}
  ) => {
    return {
      style: getVarsStyle(config),
      class: getOnLoadClass(className),
    };
  },

  /**
   * Creates animation configuration for view-triggered animations
   * Returns both CSS variables string, the appropriate class name, and the attachment
   *
   * @example
   * ```typescript
   * const { style, class: className, attachment } = animate.onView(presets.slideUp());
   * // Returns: { style: "--anim-opacity-start: 0; --anim-translate-y: 20px", class: "css-motion css-motion--view", attachment: observeOnView }
   * ```
   */
  onView: (
    config: AnimationConfig,
    {
      class: className,
      observerOptions,
    }: { class?: string; observerOptions?: IntersectionObserverOptions } = {}
  ) => {
    return {
      style: getVarsStyle(config),
      class: getOnViewClass(className),
      [createAttachmentKey()]: observeOnView(observerOptions),
    };
  },
} as const;
